import React, { useState, useEffect, useContext } from 'react';
import Modal from 'react-modal';
import { AppCtx } from '../../../AppCtx';
import yjsDocumentService from '../../../infrastructure/sync/YjsDocumentService';

// Common system fonts to test for availability
const COMMON_FONTS = [
  // Windows Fonts
  'Arial', 'Arial Black', 'Arial Narrow', 'Arial Rounded MT Bold',
  'Calibri', 'Cambria', 'Cambria Math', 'Candara', 'Comic Sans MS',
  'Consolas', 'Constantia', 'Corbel', 'Courier New', 'Ebrima', 
  'Franklin Gothic Medium', 'Gabriola', 'Gadugi', 'Georgia', 'Impact', 
  'Lucida Console', 'Lucida Sans Unicode', 'Malgun Gothic', 'Microsoft Himalaya', 
  'Microsoft JhengHei', 'Microsoft New Tai Lue', 'Microsoft PhagsPa', 
  'Microsoft Tai Le', 'Microsoft YaHei', 'Microsoft Yi Baiti', 'MingLiU-ExtB',
  'Mongolian Baiti', 'MS Gothic', 'MS Mincho', 'MS PGothic', 'MS PMincho', 
  'MV Boli', 'Myanmar Text', 'Nirmala UI', 'Palatino Linotype', 'Segoe Print', 
  'Segoe Script', 'Segoe UI', 'Segoe UI Historic', 'Segoe UI Emoji', 
  'Segoe UI Symbol', 'SimSun', 'Sitka', 'Sylfaen', 'Symbol', 'Tahoma',
  'Times New Roman', 'Trebuchet MS', 'Verdana', 'Webdings', 'Wingdings', 
  'Yu Gothic',
  
  // macOS Fonts
  'American Typewriter', 'Andale Mono', 'Apple Braille', 'Apple Color Emoji',
  'Apple SD Gothic Neo', 'Apple Symbols', 'AppleGothic', 'AppleMyungjo',
  'Avenir', 'Avenir Next', 'Avenir Next Condensed', 'Baskerville', 'Big Caslon',
  'Bodoni 72', 'Bodoni 72 Oldstyle', 'Bodoni 72 Smallcaps', 'Bradley Hand',
  'Brush Script MT', 'Chalkboard', 'Chalkboard SE', 'Chalkduster', 'Charter',
  'Cochin', 'Copperplate', 'Courier', 'Didot', 'DIN Alternate', 'DIN Condensed',
  'Futura', 'Geneva', 'Gill Sans', 'Helvetica', 'Helvetica Neue', 'Herculanum',
  'Hoefler Text', 'Iowan Old Style', 'Kailasa', 'Kohinoor Bangla',
  'Kohinoor Devanagari', 'Kohinoor Telugu', 'Lao Sangam MN', 'Lucida Grande',
  'Luminari', 'Marker Felt', 'Menlo', 'Monaco', 'Noteworthy', 'Optima',
  'Palatino', 'Papyrus', 'Phosphate', 'Rockwell', 'Savoye LET', 'SignPainter',
  'Skia', 'Snell Roundhand', 'Thonburi', 'Times', 'Trattatello', 'Zapfino',
  
  // Linux Fonts
  'DejaVu Sans', 'DejaVu Sans Mono', 'DejaVu Serif', 'Droid Sans', 'Droid Sans Mono',
  'Droid Serif', 'FreeMono', 'FreeSans', 'FreeSerif', 'Liberation Mono',
  'Liberation Sans', 'Liberation Serif', 'Linux Libertine', 'Noto Sans',
  'Noto Serif', 'Open Sans', 'Oxygen', 'Roboto', 'Source Code Pro', 'Source Sans Pro',
  'Source Serif Pro', 'Ubuntu', 'Ubuntu Condensed', 'Ubuntu Mono',
  
  // Generic/Cross-platform
  'cursive', 'fantasy', 'monospace', 'sans-serif', 'serif'
];

const FontSelectionModal = ({ isOpen, onClose, onConfirm, confirmLabel = 'Eksportuj' }) => {
  const { selectedFile } = useContext(AppCtx);
  const [fonts, setFonts] = useState({
    title: { name: 'Times New Roman', bold: true, size: '16', color: '#000000' },
    text: { name: 'Times New Roman', bold: false, size: '20', color: '#000000' },
    chord: { name: 'Times New Roman', bold: true, size: '12', color: '#FF0000' },
    meta: { name: 'Times New Roman', bold: true, size: '9', color: '#999999' },
    header: { name: 'Arial', bold: false, size: '10', color: '#666666' }
  });
  const [availableFonts, setAvailableFonts] = useState(COMMON_FONTS);
  const [isDetectingFonts, setIsDetectingFonts] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exportSettings, setExportSettings] = useState({
    includeChords: true
  });
  const [presets, setPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState('');
  const [showPresetInput, setShowPresetInput] = useState(false);

  useEffect(() => {
    if (isOpen && selectedFile) {
      // Reset advanced view state when modal opens
      setShowAdvanced(false);
      
      const wsId = `songs-${selectedFile.name}`;
      const userSettings = yjsDocumentService.getUserSettings(wsId);
      if (userSettings.docFonts) {
        // Merge saved settings with defaults to ensure all properties exist
        const defaultFonts = {
          title: { name: 'Times New Roman', bold: true, size: '16', color: '#2E74B5' },
          text: { name: 'Times New Roman', bold: false, size: '20', color: '#000000' },
          chord: { name: 'Times New Roman', bold: true, size: '12', color: '#FF0000' },
          meta: { name: 'Times New Roman', bold: true, size: '9', color: '#999999' },
          header: { name: 'Arial', bold: false, size: '10', color: '#666666' }
        };
        setFonts({
          title: { ...defaultFonts.title, ...userSettings.docFonts.title },
          text: { ...defaultFonts.text, ...userSettings.docFonts.text },
          chord: { ...defaultFonts.chord, ...userSettings.docFonts.chord },
          meta: { ...defaultFonts.meta, ...userSettings.docFonts.meta },
          header: { ...defaultFonts.header, ...userSettings.docFonts.header }
        });
      }
      if (userSettings.exportSettings) {
        setExportSettings(userSettings.exportSettings);
      }
      // Load presets
      if (userSettings.fontPresets) {
        setPresets(userSettings.fontPresets);
      } else {
        // Set default presets if none exist
        const defaultPresets = [
          {
            name: 'Domyślny',
            fonts: {
              title: { name: 'Times New Roman', bold: true, size: '16', color: '#2E74B5' },
              text: { name: 'Times New Roman', bold: false, size: '20', color: '#000000' },
              chord: { name: 'Times New Roman', bold: true, size: '12', color: '#FF0000' },
              meta: { name: 'Times New Roman', bold: true, size: '9', color: '#999999' },
              header: { name: 'Arial', bold: false, size: '10', color: '#666666' }
            },
            exportSettings: { includeChords: true },
            createdAt: new Date().toISOString()
          },
          {
            name: 'Tylko tekst',
            fonts: {
              title: { name: 'Arial', bold: true, size: '18', color: '#000000' },
              text: { name: 'Arial', bold: false, size: '14', color: '#000000' },
              chord: { name: 'Arial', bold: true, size: '10', color: '#FF0000' },
              meta: { name: 'Arial', bold: false, size: '10', color: '#666666' },
              header: { name: 'Arial', bold: false, size: '9', color: '#999999' }
            },
            exportSettings: { includeChords: false },
            createdAt: new Date().toISOString()
          },
          {
            name: 'Duży druk',
            fonts: {
              title: { name: 'Arial', bold: true, size: '20', color: '#000000' },
              text: { name: 'Arial', bold: false, size: '16', color: '#000000' },
              chord: { name: 'Arial', bold: true, size: '14', color: '#0000FF' },
              meta: { name: 'Arial', bold: true, size: '12', color: '#333333' },
              header: { name: 'Arial', bold: false, size: '11', color: '#555555' }
            },
            exportSettings: { includeChords: true },
            createdAt: new Date().toISOString()
          },
          {
            name: 'Elegancki pisany',
            fonts: {
              title: { name: 'Georgia', bold: true, size: '18', color: '#1A472A' },
              text: { name: 'Georgia', bold: false, size: '14', color: '#2C2C2C' },
              chord: { name: 'Palatino Linotype', bold: true, size: '12', color: '#8B4513' },
              meta: { name: 'Georgia', bold: false, size: '10', color: '#666666' },
              header: { name: 'Georgia', bold: false, size: '11', color: '#555555' }
            },
            exportSettings: { includeChords: true },
            createdAt: new Date().toISOString()
          },
          {
            name: 'Ręczny styl',
            fonts: {
              title: { name: 'Comic Sans MS', bold: true, size: '16', color: '#2E4BC6' },
              text: { name: 'Comic Sans MS', bold: false, size: '13', color: '#000000' },
              chord: { name: 'Comic Sans MS', bold: true, size: '11', color: '#D2691E' },
              meta: { name: 'Comic Sans MS', bold: false, size: '9', color: '#696969' },
              header: { name: 'Comic Sans MS', bold: false, size: '10', color: '#808080' }
            },
            exportSettings: { includeChords: true },
            createdAt: new Date().toISOString()
          },
          {
            name: 'Klasyczny kursywny',
            fonts: {
              title: { name: 'Palatino Linotype', bold: true, size: '17', color: '#4A4A4A' },
              text: { name: 'Book Antiqua', bold: false, size: '14', color: '#1C1C1C' },
              chord: { name: 'Trebuchet MS', bold: true, size: '12', color: '#B22222' },
              meta: { name: 'Palatino Linotype', bold: false, size: '10', color: '#777777' },
              header: { name: 'Book Antiqua', bold: false, size: '10', color: '#666666' }
            },
            exportSettings: { includeChords: true },
            createdAt: new Date().toISOString()
          }
        ];
        setPresets(defaultPresets);
      }
    }
  }, [isOpen, selectedFile]);

  // Enhanced function to detect available fonts
  const detectAvailableFonts = async () => {
    setIsDetectingFonts(true);
    const detectedFonts = new Set();
    
    // Method 1: Try to use the new Font Access API (Chrome 103+)
    if ('queryLocalFonts' in window) {
      try {
        const permission = await navigator.permissions.query({ name: 'local-fonts' });
        if (permission.state === 'granted' || permission.state === 'prompt') {
          const fonts = await window.queryLocalFonts();
          fonts.forEach(font => {
            detectedFonts.add(font.family);
          });
          console.log(`Found ${detectedFonts.size} fonts using Font Access API`);
        }
      } catch (error) {
        console.log('Font Access API not available or permission denied:', error);
      }
    }
    
    // Method 2: Canvas-based detection (fallback for broader compatibility)
    if (detectedFonts.size === 0) {
      console.log('Using canvas-based font detection...');
      const testString = 'mmmmmmmmmmlli';
      const testSize = '72px';
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Measure baseline width with a known monospace font
      ctx.font = testSize + ' monospace';
      const baselineWidth = ctx.measureText(testString).width;

      for (const font of COMMON_FONTS) {
        try {
          ctx.font = testSize + ' "' + font + '", monospace';
          const width = ctx.measureText(testString).width;
          if (Math.abs(width - baselineWidth) > 1) { // Allow for small rounding differences
            detectedFonts.add(font);
          }
        } catch (error) {
          // Skip fonts that cause errors
          continue;
        }
      }
      console.log(`Found ${detectedFonts.size} fonts using canvas detection`);
    }
    
    // Convert to sorted array and ensure we have at least some basic fonts
    const fontsArray = Array.from(detectedFonts).sort();
    if (fontsArray.length === 0) {
      // Fallback to basic web-safe fonts
      setAvailableFonts(['Arial', 'Times New Roman', 'Courier New', 'Helvetica', 'Georgia', 'Verdana']);
    } else {
      setAvailableFonts(fontsArray);
    }
    
    setIsDetectingFonts(false);
    return fontsArray;
  };

  useEffect(() => {
    if (isOpen) {
      detectAvailableFonts();
    }
  }, [isOpen]);

  const handleChange = (type, field, value) => {
    setFonts(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const savePreset = () => {
    if (!newPresetName.trim()) return;
    
    const preset = {
      name: newPresetName.trim(),
      fonts: { ...fonts },
      exportSettings: { ...exportSettings },
      createdAt: new Date().toISOString()
    };
    
    const updatedPresets = [...presets, preset];
    setPresets(updatedPresets);
    
    // Save to user settings
    if (selectedFile) {
      const wsId = `songs-${selectedFile.name}`;
      yjsDocumentService.updateUserSettings(wsId, { fontPresets: updatedPresets });
    }
    
    setNewPresetName('');
    setShowPresetInput(false);
  };

  const loadPreset = (preset) => {
    setFonts(preset.fonts);
    setExportSettings(preset.exportSettings);
  };

  const deletePreset = (presetIndex) => {
    const updatedPresets = presets.filter((_, index) => index !== presetIndex);
    setPresets(updatedPresets);
    
    // Update user settings
    if (selectedFile) {
      const wsId = `songs-${selectedFile.name}`;
      yjsDocumentService.updateUserSettings(wsId, { fontPresets: updatedPresets });
    }
  };

  const handleConfirm = () => {
    if (selectedFile) {
      const wsId = `songs-${selectedFile.name}`;
      yjsDocumentService.updateUserSettings(wsId, { 
        docFonts: fonts,
        exportSettings: exportSettings 
      });
    }
    onConfirm({ fonts, exportSettings });
    onClose();
  };

  const CustomFontSelect = ({ value, onChange, type }) => (
    <select
      value={value}
      onChange={onChange}
      disabled={isDetectingFonts}
      style={{ 
        width: '100%', 
        padding: '6px', 
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        fontSize: '13px',
        backgroundColor: 'var(--background-color)',
        color: 'var(--text-color)',
        opacity: isDetectingFonts ? 0.6 : 1
      }}
    >
      {isDetectingFonts ? (
        <option>Wykrywanie czcionek...</option>
      ) : (
        availableFonts.map(font => (
          <option 
            key={font} 
            value={font}
            style={{ fontFamily: font }}
          >
            {font}
          </option>
        ))
      )}
    </select>
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={{
      content: { 
        top: '50%', 
        left: '50%', 
        right: 'auto', 
        bottom: 'auto', 
        marginRight: '-50%', 
        transform: 'translate(-50%, -50%)', 
        width: '420px',
        maxHeight: '80vh',
        overflow: 'auto',
        padding: '16px',
        backgroundColor: 'var(--background-color)',
        color: 'var(--text-color)',
        border: '1px solid var(--border-color)'
      },
      overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 10003 }
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--text-color)' }}>Export do DOCX</h2>
      </div>

      {showAdvanced && (
        <>
          <p style={{ textAlign: 'center', color: 'var(--text-color-secondary)', marginBottom: '12px' }}>
            {isDetectingFonts ? 
              'Wykrywanie czcionek systemowych...' : 
              `Dostępne czcionki systemowe: ${availableFonts.length}`
            }
          </p>
          {['title', 'text', 'chord', 'meta', 'header'].map(type => (
            <div key={type} style={{ 
              marginBottom: '6px', 
              padding: '8px', 
              borderRadius: '4px',
              backgroundColor: 'var(--background-color-alt)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: '600', color: 'var(--text-color)', fontSize: '14px' }}>
                  {type === 'title' ? 'Tytuł' : 
                   type === 'text' ? 'Tekst' : 
                   type === 'chord' ? 'Akordy' : 
                   type === 'meta' ? 'Meta' : 
                   type === 'header' ? 'Nagłówek' : type.charAt(0).toUpperCase() + type.slice(1)}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ minWidth: '50px', fontSize: '12px', color: 'var(--text-color)' }}>Czcionka:</label>
                <div style={{ flex: 1 }}>
                  <CustomFontSelect 
                    value={fonts[type].name}
                    onChange={(e) => handleChange(type, 'name', e.target.value)}
                    type={type}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ minWidth: '50px', fontSize: '12px', color: 'var(--text-color)' }}>Rozmiar:</label>
                <input
                  type="number"
                  value={fonts[type].size}
                  onChange={(e) => handleChange(type, 'size', e.target.value)}
                  placeholder="Size"
                  min="8"
                  max="72"
                  style={{ 
                    width: '60px', 
                    padding: '6px', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: 'var(--background-color)',
                    color: 'var(--text-color)'
                  }}
                />
                <span style={{ color: 'var(--text-color-secondary)', fontSize: '12px' }}>px</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', marginLeft: '8px' }}>
                  <input
                    type="checkbox"
                    checked={fonts[type].bold}
                    onChange={(e) => handleChange(type, 'bold', e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ color: 'var(--text-color)' }}>Pogrubiony</span>
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ minWidth: '50px', fontSize: '12px', color: 'var(--text-color)' }}>Kolor:</label>
                <input
                  type="color"
                  value={fonts[type].color || '#000000'}
                  onChange={(e) => handleChange(type, 'color', e.target.value)}
                  style={{ 
                    width: '40px', 
                    height: '32px', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '4px',
                    padding: '2px',
                    cursor: 'pointer',
                    backgroundColor: 'var(--background-color)'
                  }}
                />
                <span style={{ 
                  fontSize: '11px', 
                  color: 'var(--text-color-secondary)',
                  fontFamily: 'monospace'
                }}>
                  {fonts[type].color || '#000000'}
                </span>
              </div>
            </div>
          ))}
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
        {showAdvanced && (
          <>
            {/* Presety */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '10px', 
              borderRadius: '4px',
              backgroundColor: 'var(--background-color-alt)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-color)' }}>
                Presety ustawień
              </div>
              <button
                onClick={() => setShowPresetInput(!showPresetInput)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {showPresetInput ? 'Anuluj' : '+ Nowy'}
              </button>
            </div>

            {showPresetInput && (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                padding: '10px', 
                borderRadius: '4px',
                backgroundColor: 'var(--background-color-alt)',
                border: '1px solid var(--border-color)'
              }}>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="Nazwa presetu (np. 'Dla starszych', 'Compact')"
                  style={{
                    flex: 1,
                    padding: '6px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: 'var(--background-color)',
                    color: 'var(--text-color)'
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && savePreset()}
                />
                <button
                  onClick={savePreset}
                  disabled={!newPresetName.trim()}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: newPresetName.trim() ? '#4CAF50' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: newPresetName.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  Zapisz
                </button>
              </div>
            )}

            {presets.length > 0 && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px', 
                padding: '10px', 
                borderRadius: '4px',
                backgroundColor: 'var(--background-color-alt)',
                border: '1px solid var(--border-color)'
              }}>
                {presets.map((preset, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '6px 8px',
                    borderRadius: '3px',
                    backgroundColor: 'var(--background-color)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-color)' }}>
                      {preset.name}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => loadPreset(preset)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '11px',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        Wczytaj
                      </button>
                      <button
                        onClick={() => deletePreset(index)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '11px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ustawienia eksportu */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '10px', 
              borderRadius: '4px',
              backgroundColor: 'var(--background-color-alt)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-color)' }}>
                Ustawienia eksportu
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '10px', 
              borderRadius: '4px',
              backgroundColor: 'var(--background-color-alt)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ fontSize: '13px', color: 'var(--text-color-secondary)' }}>
                Dołącz akordy
              </div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                cursor: 'pointer' 
              }}>
                <input
                  type="checkbox"
                  checked={exportSettings.includeChords}
                  onChange={(e) => setExportSettings(s => ({ ...s, includeChords: e.target.checked }))}
                  style={{ margin: 0 }}
                />
                <span style={{ color: 'var(--text-color)' }}>
                  {exportSettings.includeChords ? 'Tak' : 'Nie'}
                </span>
              </label>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', alignItems: 'center' }}>
        <button onClick={handleConfirm} style={{ 
          padding: '10px 18px', 
          backgroundColor: '#4CAF50', 
          color: 'white', 
          border: 'none', 
          borderRadius: '6px', 
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          {confirmLabel}
        </button>

        <button
          aria-label={showAdvanced ? 'Ukryj dodatkowe opcje' : 'Pokaż dodatkowe opcje'}
          onClick={() => setShowAdvanced(s => !s)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--border-color)',
            background: showAdvanced ? 'var(--background-color-alt)' : 'var(--background-color)',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: 'var(--text-color)',
            boxShadow: showAdvanced ? 'inset 0 1px 3px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.1)'
          }}
          title={showAdvanced ? 'Ukryj opcje' : 'Pokaż opcje dostosowania'}
        >
          ⚙
          <span>Dostosuj</span>
        </button>

        <button onClick={onClose} style={{ 
          padding: '10px 18px', 
          backgroundColor: '#f44336', 
          color: 'white', 
          border: 'none', 
          borderRadius: '6px', 
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          Anuluj
        </button>
      </div>
    </Modal>
  );
};

export default FontSelectionModal;
